import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import { devWarn } from '../luna-common';
import { getTheme, setTheme, type ThemeMode } from '../../theming';

const THEMES: readonly ThemeMode[] = ['blue', 'sand', 'slate'];
const THEME_LABELS: Record<ThemeMode, string> = {
  blue: 'Blue',
  sand: 'Sand',
  slate: 'Slate',
};

function isThemeMode(value: string): value is ThemeMode {
  return (THEMES as readonly string[]).includes(value);
}

@customElement('luna-appearance-switcher')
export class LunaAppearanceSwitcher extends LitPage {
  private _selected: ThemeMode | null = null;

  @property({ attribute: 'selected' })
  get selected(): ThemeMode | null {
    return this._selected;
  }

  set selected(value: string | null) {
    const next = String(value ?? '');
    if (next === '') {
      this._selected = null;
      return;
    }
    if (!isThemeMode(next)) {
      devWarn(`luna-appearance-switcher: unknown selected "${next}" — using getTheme()`);
      this._selected = getTheme();
      return;
    }
    this._selected = next;
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (this._selected === null) {
      this._selected = getTheme();
    }
  }

  private effectiveSelection(): ThemeMode {
    return this._selected ?? getTheme();
  }

  private select(mode: ThemeMode): void {
    setTheme(mode);
    this.selected = mode;
    this.dispatchEvent(
      new CustomEvent('luna-select', {
        detail: { theme: mode },
        bubbles: true,
        composed: true,
      })
    );
  }

  content() {
    const selected = this.effectiveSelection();

    return html`
      <div class="luna-appearance-switcher" role="radiogroup" aria-label="Appearance">
        ${THEMES.map(
          (mode) => html`
            <button
              type="button"
              role="radio"
              aria-checked=${selected === mode ? 'true' : 'false'}
              class="luna-appearance-switcher__item ${selected === mode ? 'is-selected' : ''}"
              @click=${() => this.select(mode)}
            >
              <span
                class="luna-appearance-switcher__swatch luna-appearance-switcher__swatch--${mode}"
                aria-hidden="true"
              ></span>
              <span class="mbw-type-label-chip">${THEME_LABELS[mode]}</span>
            </button>
          `
        )}
      </div>
    `;
  }

  styles() {
    return css`
      .luna-appearance-switcher {
        display: flex;
        align-items: center;
        width: 540px;
        max-width: 100%;
        height: 56px;
        border-radius: var(--luna-radius-btn);
        background: #f0f2f6; /* pinned: figma Appearance Switcher */
        box-shadow:
          inset 0 0 0 1px #d3ddec,
          0 3px 0 #cdd8ec,
          0 6px 14px rgba(20, 40, 90, 0.35); /* pinned: figma Appearance Switcher */
      }

      .luna-appearance-switcher__item {
        flex: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        height: 56px;
        padding: 0 20px;
        box-sizing: border-box;
        border: none;
        background: transparent;
        cursor: pointer;
        color: var(--mbw-text-body);
      }

      .luna-appearance-switcher__item + .luna-appearance-switcher__item {
        border-left: 1px solid var(--mbw-card-hairline);
      }

      /* BR-96: Figma clips children to the track radius; CSS must carry the radius
         on the outer items so the selected fill doesn't paint square corners.
         overflow:hidden on the track would clip the :focus-visible ring — do not use. */
      .luna-appearance-switcher__item:first-child {
        border-radius: var(--luna-radius-btn) 0 0 var(--luna-radius-btn);
      }

      .luna-appearance-switcher__item:last-child {
        border-radius: 0 var(--luna-radius-btn) var(--luna-radius-btn) 0;
      }

      .luna-appearance-switcher__item.is-selected {
        background: var(--mbw-surface-card);
        color: var(--cds-text-primary);
      }

      .luna-appearance-switcher__item:focus-visible {
        outline: none;
        box-shadow: 0 0 0 5px #ffffff, 0 0 0 7px var(--cds-focus);
      }

      .luna-appearance-switcher__swatch {
        width: 22px;
        height: 22px;
        border-radius: 6px;
      }

      .luna-appearance-switcher__swatch--blue {
        background-image: linear-gradient(180deg, #4a9bff 0%, #1358d8 14%, #0a46c4 60%, #0d51d6 100%); /* pinned: figma Appearance Switcher */
      }

      .luna-appearance-switcher__swatch--sand {
        background-image: linear-gradient(180deg, #7a6c5e 0%, #635546 55%, #4f4339 100%); /* pinned: figma Appearance Switcher */
      }

      .luna-appearance-switcher__swatch--slate {
        background-image: linear-gradient(180deg, #5a6068 0%, #41474e 16%, #2f343a 60%, #3a4046 100%); /* pinned: figma Appearance Switcher */
      }
    `;
  }
}
